import React, { useEffect, useState, useMemo } from "react";
import { PageHeader, Modal, Toast, EmptyState, ExportButtons } from "@/components/Layout";
import api, { API, soft } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ViewOnly from "@/components/ViewOnly";
import { postImport } from "@/lib/importJob";
import { lock } from "@/lib/perm";
import { groupOrgUnitsByParent } from "@/lib/orgTree";
import PurgeButton from "@/components/PurgeButton";
import RowActions from "@/components/RowActions";
import ImportFileStep from "@/components/ImportFileStep";
import Select from "@/components/Select";

/**
 * Structura organizatorică (ierarhie strictă cu nivele).
 * Nivel 1 = Angajatorul (rădăcină)
 * Nivel 2 = Structurile sub Nivel 1
 * Nivel 3 = Structurile sub Nivel 2 etc.
 * Un „Punct de lucru / sediu" poate fi selectat per structură; dacă e gol, se moștenește de la părinte.
 */
export default function OrgStructure() {
  const { hasModule, has } = useAuth();
  const postFilterAvailable = hasModule("stat_functii");
  const [personnelCategories, setPersonnelCategories] = useState([]);
  const [postCategories, setPostCategories] = useState([]);
  // filtre pentru organigramă: gol = fără filtru (organigrama completă)
  const [chartFilters, setChartFilters] = useState({ units: [], personnel: [], posts: [] });
  const [unitFilterText, setUnitFilterText] = useState("");
  const [units, setUnits] = useState([]);
  const [empls, setEmpls] = useState([]);
  const [locations, setLocations] = useState([]);
  const [employer, setEmployer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [msg, setMsg] = useState({text:"",type:"info"});
  const [showArchive, setShowArchive] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [chartBusy, setChartBusy] = useState(false);
  // Arborele se afișează pe nivele: primele două (rădăcina + copiii ei) sunt vizibile, restul se deschid cu săgeata.
  // `expanded[id]` suprascrie implicitul (rădăcinile — adâncimea 0 — sunt deschise, restul închise).
  const [expanded, setExpanded] = useState({});
  const isExpanded = (id, depth) => expanded[id] ?? (depth === 0);

  const load = async () => {
    try {
      const [u, e, l, emp] = await Promise.all([
        api.get("/mdm/org-units"),
        soft(api.get("/mdm/employments"), []),
        soft(api.get("/mdm/locations"), []),
        soft(api.get("/mdm/employer"), null),
      ]);
      setUnits(u.data); setEmpls(e.data); setLocations(l.data); setEmployer(emp.data);
    } catch (err) {
      setMsg({text: err?.response?.data?.detail || "Eroare la încărcare", type:"error"});
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    api.get("/config").then(r => setPersonnelCategories(r.data.personnel_categories || [])).catch(() => {});
  }, []);
  // „Categorie de post” există doar cu Stat de Funcții licențiat
  useEffect(() => {
    if (!postFilterAvailable) { setPostCategories([]); setChartFilters(f => ({ ...f, posts: [] })); return; }
    if (has("stat_functii.view")) api.get("/stat-functii/config").then(r => setPostCategories((r.data.post_categories || []).map(c => c.name))).catch(() => {});
  }, [postFilterAvailable]);

  // Rezolvă „location efectiv" moștenind de la părinte când e null
  const effectiveLocation = (unitId) => {
    let cur = units.find(u => u.id === unitId);
    const seen = new Set();
    while (cur) {
      if (cur.location_id) return locations.find(l => l.id === cur.location_id);
      if (seen.has(cur.id)) return null;
      seen.add(cur.id);
      if (!cur.parent_id) return null;
      cur = units.find(u => u.id === cur.parent_id);
    }
    return null;
  };

  const save = async () => {
    if (!editing.code?.trim() || !editing.name?.trim()) {
      setMsg({text:"Cod și denumire sunt obligatorii",type:"error"}); return;
    }
    try {
      const body = {
        code: editing.code.trim(),
        name: editing.name.trim(),
        parent_id: editing.parent_id || null,
        manager_work_report_id: editing.manager_employment_id || null,
        location_id: editing.location_id || null,
        active: editing.active
      };
      if (editing.id) await api.put(`/mdm/org-units/${editing.id}`, body);
      else await api.post("/mdm/org-units", body);
      if (body.parent_id) setExpanded(x => ({ ...x, [body.parent_id]: true }));
      setEditing(null); load();
      setMsg({text:"Salvat",type:"success"}); setTimeout(()=>setMsg({text:""}),1500);
    } catch(e){ setMsg({text:e?.response?.data?.detail||"Eroare",type:"error"}); }
  };

  const toggle = async (u) => {
    try {
      if (u.active) {
        if (!window.confirm(`Dezactivați structura „${u.name}"?`)) return;
        await api.delete(`/mdm/org-units/${u.id}`);
      } else {
        await api.put(`/mdm/org-units/${u.id}`, {
          code: u.code, name: u.name, parent_id: u.parent_id, active: true,
          manager_work_report_id: u.manager_work_report_id,
          location_id: u.location_id,
        });
      }
      load();
    } catch(e) {
      setMsg({text:e?.response?.data?.detail||"Eroare la modificarea stării",type:"error"});
    }
  };

  const downloadTemplate = () => {
    fetch(`${API}/mdm/org-units/import/template`, { credentials: "include" })
      .then(r => r.blob()).then(b => {
        const a = document.createElement("a"); a.href = URL.createObjectURL(b);
        a.download = "template_structura_organizatorica.xlsx"; a.click();
      });
  };
  // aruncă la eroare: mesajul exact al serverului îl afișează ImportFileStep sub butonul „Importă”
  const uploadFile = async (file, onProgress) => {
    const r = await postImport("/mdm/org-units/import", file, onProgress);
    setImportResult(r.data); load();
  };
  // Organigrama (PDF) — data generării e înscrisă în document și în numele fișierului.
  const toggleFilter = (key, value) => setChartFilters(f => ({
    ...f, [key]: f[key].includes(value) ? f[key].filter(x => x !== value) : [...f[key], value] }));
  const activeFilterCount = chartFilters.units.length + chartFilters.personnel.length + chartFilters.posts.length;
  const generateChart = async () => {
    setChartBusy(true);
    try {
      const qs = new URLSearchParams();
      chartFilters.units.forEach(v => qs.append("org_unit_id", v));
      chartFilters.personnel.forEach(v => qs.append("personnel_category", v));
      chartFilters.posts.forEach(v => qs.append("post_category", v));
      const r = await fetch(`${API}/mdm/org-units/chart.pdf${qs.toString() ? `?${qs}` : ""}`, { credentials: "include" });
      if (!r.ok) {
        let detail = "Eroare la generarea organigramei";
        try { detail = (await r.json()).detail || detail; } catch {}
        throw new Error(detail);
      }
      const blob = await r.blob();
      const m = /filename="([^"]+)"/.exec(r.headers.get("Content-Disposition") || "");
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
      a.download = m ? m[1] : "organigrama.pdf"; a.click();
      setMsg({text:"Organigrama a fost generată și descărcată",type:"success"}); setTimeout(()=>setMsg({text:""}),2500);
    } catch(e) { setMsg({text: e.message || "Eroare la generarea organigramei", type:"error"}); }
    finally { setChartBusy(false); }
  };

  const newUnit = (parent=null) => setEditing({
    code:"", name:"", parent_id:parent, manager_employment_id:"", location_id:"", active:true
  });

  const byParent = useMemo(() => groupOrgUnitsByParent(units), [units]);

  // Arborele principal arată doar structurile active. O structură dezactivată nu mai
  // apare aici (doar în Arhivă) — dar tot recurăm prin ea ca să nu „ascundem" din
  // greșeală subunități active rămase sub un părinte arhivat (nu creștem depth în acest caz).
  const renderTree = (parentId, depth=0) => {
    const kids = (byParent[parentId]||[]).sort((a,b)=> a.code.localeCompare(b.code));
    return kids.flatMap(u => {
      if (!u.active) return renderTree(u.id, depth);
      const childRows = renderTree(u.id, depth+1);
      const hasKids = childRows.length > 0;
      const open = hasKids && isExpanded(u.id, depth);
      const mgrEmpl = empls.find(e => e.id === u.manager_work_report_id);
      const mgrName = mgrEmpl?.user_name || mgrEmpl?.marca || "—";
      const loc = effectiveLocation(u.id);
      const locOwn = u.location_id ? locations.find(l => l.id === u.location_id) : null;
      // nivel 1 = navy, nivel 2 = albastru deschis, nivel 3 = verde deschis, restul pe alb (ca să se vadă dintr-o privire ierarhia)
      const tone = u.level === 1
        ? { bg: "linear-gradient(90deg, var(--navy) 0%, var(--navy-3) 100%)", border: "3px solid var(--navy)", text: "#FFFFFF", sub: "var(--tint-200)", badgeBg: "rgba(255,255,255,0.18)", badgeFg: "#FFFFFF" }
        : u.level === 2
        ? { bg: "var(--tint-100)", border: "3px solid var(--blue-primary)", text: "var(--navy)", sub: "var(--navy-3)", badgeBg: "#FFFFFF", badgeFg: "var(--blue-primary)" }
        : u.level === 3
        ? { bg: "#DCFCE7", border: "3px solid #15803D", text: "var(--navy)", sub: "#166534", badgeBg: "#FFFFFF", badgeFg: "#15803D" }
        : { bg: "white", border: "1px solid #E2E8F0", text: "inherit", sub: "#64748B", badgeBg: "var(--tint-50)", badgeFg: "var(--blue-primary)" };
      return [
        <React.Fragment key={u.id}>
          <div className="card fade-up" style={{ padding:"12px 14px", marginLeft: depth*24, marginBottom:6,
              display:"flex", alignItems:"center", gap:12,
              borderLeft: tone.border, background: tone.bg, color: tone.text }}
              data-testid={`unit-row-${u.code}`}>
            {hasKids
              ? <button type="button" onClick={()=>setExpanded(x=>({...x,[u.id]:!open}))} data-testid={`expand-${u.code}`}
                  title={open ? "Restrânge nivelul următor" : "Afișează nivelul următor"} aria-expanded={open}
                  style={{border:"none",background:"#F1F5F9",borderRadius:6,width:26,height:26,cursor:"pointer",color:"var(--navy)",fontSize:12,
                          flexShrink:0,transform: open ? "none" : "rotate(-90deg)",transition:"transform .15s"}}>▼</button>
              : <span style={{width:26,flexShrink:0}} />}
            <span style={{
              fontSize:10, fontWeight:800, letterSpacing:"0.05em",
              padding:"2px 8px", borderRadius:99, background:tone.badgeBg, color:tone.badgeFg
            }} title={`Nivel ${u.level}`}>N{u.level}</span>
            <span style={{ fontSize:11, color:tone.sub, fontWeight:700, minWidth:60 }}>{u.code}</span>
            <div style={{flex:1}}>
              <div style={{fontWeight:600}}>{u.name}</div>
              <div style={{fontSize:11, color:tone.sub, marginTop:2, display:"flex", gap:12, flexWrap:"wrap"}}>
                <span>Manager: {mgrName}</span>
                {loc && <span>Punct de lucru: <b>{loc.name}</b>{!locOwn && <em style={{fontSize:10, color:tone.sub, opacity:0.75}}> (moștenit)</em>}</span>}
              </div>
            </div>
            <RowActions testid={`row-menu-${u.code}`} items={[
              {label:"Vizualizează", icon:"eye", onClick:()=>setEditing({...u, manager_employment_id: u.manager_work_report_id||"", location_id: u.location_id||"", view:true}), testid:`view-unit-${u.code}`},
              {label:"Editează", icon:"edit", onClick:()=>setEditing({...u, manager_employment_id: u.manager_work_report_id||"", location_id: u.location_id||""}), testid:`edit-unit-${u.code}`, ...lock(has("org_units.update"))},
              {label:"+ Subunitate", icon:"list", onClick:()=>newUnit(u.id), testid:`add-sub-${u.code}`, ...lock(has("org_units.create"))},
              {label:"Dezactivează", icon:"close", danger:true, btnDanger:true, onClick:()=>toggle(u), testid:`toggle-unit-${u.code}`, ...lock(has("org_units.deactivate"))},
            ]}><PurgeButton entity="org-unit" id={u.id} onDone={load} /></RowActions>
          </div>
        </React.Fragment>,
        ...(open ? childRows : []),
      ];
    });
  };

  const archivedUnits = useMemo(
    () => units.filter(u => !u.active).sort((a,b)=>a.code.localeCompare(b.code)),
    [units]
  );

  // Filtrez lista de părinți în modal — nu putem alege un descendent ca părinte (ciclu)
  const validParents = (targetId) => {
    if (!targetId) return units.filter(u => u.active);
    // Descendenții lui targetId — nu pot fi părinți
    const descs = new Set();
    const walk = (id) => (units.filter(u => u.parent_id === id)).forEach(c => { descs.add(c.id); walk(c.id); });
    walk(targetId);
    return units.filter(u => u.active && u.id !== targetId && !descs.has(u.id));
  };

  return (
    <>
      <PageHeader
        title="Structură Organizatorică"
        subtitle={`Ierarhie strictă cu nivele · Nivel 1 = ${employer?.name || "Angajatorul"}`}
        actions={<div style={{display:"flex",gap:8}}>
          <ExportButtons basePath="/mdm/org-units" perm="org_units.export" />
          <button className="btn btn-secondary" onClick={downloadTemplate} data-testid="dl-org-template" {...lock(!!employer && has("org_units.import_template"))}>↓ Template Excel</button>
          <button className="btn btn-secondary" onClick={()=>setImporting(true)} data-testid="btn-import-org" {...lock(!!employer && has("org_units.import"))}>Import Excel</button>
          <button className="btn btn-secondary" onClick={()=>setShowArchive(a=>!a)} data-testid="toggle-archive-view">
            {showArchive ? "← Structură activă" : `Arhivă (${archivedUnits.length})`}
          </button>
          {!showArchive && <button className="btn btn-primary" onClick={()=>newUnit(null)} data-testid="add-root-unit" {...lock(!!employer && has("org_units.create"))}>+ Structură rădăcină</button>}
        </div>}
      />
      <Toast msg={msg.text} type={msg.type} />

      {!loading && !employer && (
        <div className="card" style={{padding:16,marginBottom:16,borderLeft:"3px solid #DC2626",background:"#FEF2F2",fontSize:13}} data-testid="no-employer-banner">
          Trebuie definit mai întâi <b>Angajatorul</b> înainte de a configura structura organizatorică. Mergi la pagina „Angajator".
        </div>
      )}

      <div className="card" style={{padding:16,marginBottom:16}} data-testid="org-chart-section">
        <div style={{display:"flex",alignItems:"center",gap:16,flexWrap:"wrap"}}>
          <div style={{flex:1,minWidth:260}}>
            <div style={{fontWeight:700,color:"var(--navy)"}}>Organigramă grafică</div>
            <div style={{fontSize:12,color:"#64748B",marginTop:2}}>
              Generează organigrama structurilor active, cu ierarhia stabilită mai jos — completă sau filtrată. Se descarcă în PDF, cu data generării înscrisă.
            </div>
          </div>
          {activeFilterCount > 0 && (
            <button className="btn btn-secondary" onClick={()=>setChartFilters({units:[],personnel:[],posts:[]})} data-testid="reset-chart-filters">
              Resetează filtrele ({activeFilterCount})
            </button>)}
          <button className="btn btn-primary" onClick={generateChart} data-testid="generate-org-chart" {...lock(!chartBusy && units.filter(u=>u.active).length>0 && has("org_units.chart"))}>
            {chartBusy ? "Se generează..." : activeFilterCount > 0 ? "Generează organigrama filtrată (PDF)" : "Generează organigrama completă (PDF)"}
          </button>
        </div>
        <details style={{marginTop:12}} open={activeFilterCount > 0}>
          <summary style={{cursor:"pointer",fontSize:13,fontWeight:600,color:"var(--blue-primary)"}} data-testid="chart-filters-toggle">Filtre organigramă</summary>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(260px,1fr))",gap:16,marginTop:12}}>
            <div>
              <div className="label">Structură organizatorică (loc de muncă)</div>
              <input className="input" placeholder="Caută structură…" value={unitFilterText} onChange={e=>setUnitFilterText(e.target.value)} style={{marginBottom:6}} />
              <div style={{maxHeight:180,overflow:"auto",border:"1px solid #E2E8F0",borderRadius:6,padding:"6px 8px"}} data-testid="chart-filter-units">
                {units.filter(u=>u.active && (!unitFilterText.trim() || `${u.code} ${u.name}`.toLowerCase().includes(unitFilterText.trim().toLowerCase())))
                  .sort((a,b)=>a.code.localeCompare(b.code)).map(u=>(
                  <label key={u.id} style={{display:"flex",gap:6,alignItems:"center",fontSize:12,marginLeft:(u.level-1)*10,padding:"2px 0"}}>
                    <input type="checkbox" checked={chartFilters.units.includes(u.id)} onChange={()=>toggleFilter("units",u.id)} data-testid={`chart-unit-${u.code}`} />
                    <span>{u.code} · {u.name}</span>
                  </label>))}
              </div>
              <div style={{fontSize:11,color:"#64748B",marginTop:4}}>Se afișează structurile bifate, cu tot ce se află sub ele.</div>
            </div>
            <div>
              <div className="label">Categorie de personal</div>
              <div style={{display:"grid",gap:4}}>
                {personnelCategories.map(c=>(
                  <label key={c} style={{display:"flex",gap:6,alignItems:"center",fontSize:13}}>
                    <input type="checkbox" checked={chartFilters.personnel.includes(c)} onChange={()=>toggleFilter("personnel",c)} data-testid={`chart-personnel-${c}`} /> {c}
                  </label>))}
              </div>
              <div style={{fontSize:11,color:"#64748B",marginTop:4}}>Doar structurile cu angajați din categoriile bifate; în casetă apare numărul lor.</div>
            </div>
            {postFilterAvailable && (
              <div>
                <div className="label">Categorie de post</div>
                <div style={{display:"grid",gap:4}}>
                  {postCategories.length === 0 && <div style={{fontSize:12,color:"#64748B"}}>Nicio categorie de post definită în Stat de Funcții.</div>}
                  {postCategories.map(c=>(
                    <label key={c} style={{display:"flex",gap:6,alignItems:"center",fontSize:13}}>
                      <input type="checkbox" checked={chartFilters.posts.includes(c)} onChange={()=>toggleFilter("posts",c)} data-testid={`chart-post-${c}`} /> {c}
                    </label>))}
                </div>
                <div style={{fontSize:11,color:"#64748B",marginTop:4}}>Doar structurile cu posturi din categoriile bifate; în casetă apare numărul lor.</div>
              </div>)}
          </div>
          <div style={{fontSize:11,color:"#64748B",marginTop:10}}>Filtrele se combină (ȘI); bifele din același filtru se adună (SAU). Fără nicio bifă se generează organigrama completă.</div>
        </details>
      </div>

      {importing && (
        <Modal title="Import Structură Organizatorică din Excel" onClose={()=>{setImporting(false); setImportResult(null);}} width={720}>
          {!importResult ? (
            <>
              <div style={{marginBottom:14,fontSize:13,color:"#334155"}}>
                <p style={{marginTop:0}}>1. Descarcă template-ul Excel (butonul „↓ Template Excel”).</p>
                <p>2. Completează câte un rând pe structură: cod, denumire, codul structurii părinte (gol = rădăcină), punctul de lucru și marca managerului (opționale). Părintele poate fi definit și mai jos în fișier.</p>
                <p>3. Structurile cu un cod deja existent se <b>actualizează</b> (denumire, părinte, punct de lucru, manager); cele noi se creează.</p>
              </div>
              <button className="btn btn-secondary" onClick={downloadTemplate}>↓ Descarcă Template Excel</button>
              <ImportFileStep accept=".xlsx" onImport={uploadFile} testId="import-org-file" />
            </>
          ) : (
            <div>
              <div style={{padding:14,background:"var(--tint-100)",borderRadius:6,marginBottom:14}} data-testid="org-import-result">
                <div style={{fontWeight:700,color:"var(--navy-3)",marginBottom:6}}>Import finalizat</div>
                <div style={{fontSize:13}}>Structuri create: <b>{importResult.created}</b></div>
                <div style={{fontSize:13}}>Structuri actualizate: <b>{importResult.updated}</b></div>
              </div>
              {importResult.errors.length > 0 && (
                <div>
                  <div style={{fontWeight:700,color:"#991B1B",marginBottom:6}}>Erori ({importResult.errors.length})</div>
                  <div style={{maxHeight:240,overflow:"auto",border:"1px solid #FCA5A5",borderRadius:6}}>
                    <table className="tbl" style={{fontSize:12}}>
                      <thead><tr><th>Rând</th><th>Eroare</th></tr></thead>
                      <tbody>{importResult.errors.map((e,i)=>(<tr key={i}><td className="tabular-nums">{e.row}</td><td>{e.error}</td></tr>))}</tbody>
                    </table>
                  </div>
                </div>
              )}
              <div style={{display:"flex",justifyContent:"flex-end",marginTop:16}}>
                <button className="btn btn-primary" onClick={()=>{setImporting(false);setImportResult(null);}}>Închide</button>
              </div>
            </div>
          )}
        </Modal>
      )}

      {showArchive ? (
        <>
          {archivedUnits.length === 0 && <EmptyState text="Nicio structură dezactivată." />}
          {archivedUnits.map(u => {
            const parent = units.find(p => p.id === u.parent_id);
            return (
              <div key={u.id} className="card fade-up" style={{ padding:"12px 14px", marginBottom:6,
                  display:"flex", alignItems:"center", gap:12, opacity:0.7, background:"#F8FAFC" }}
                  data-testid={`archived-unit-row-${u.code}`}>
                <span style={{ fontSize:10, fontWeight:800, letterSpacing:"0.05em",
                  padding:"2px 8px", borderRadius:99, background:"var(--tint-50)", color:"var(--blue-primary)"
                }} title={`Nivel ${u.level}`}>N{u.level}</span>
                <span style={{ fontSize:11, color:"#64748B", fontWeight:700, minWidth:60 }}>{u.code}</span>
                <div style={{flex:1}}>
                  <div style={{fontWeight:600}}>{u.name}</div>
                  <div style={{fontSize:11, color:"#64748B", marginTop:2}}>
                    {parent ? `Sub: ${parent.name}` : "Structură rădăcină"}
                  </div>
                </div>
                <RowActions testid={`row-menu-${u.code}`} items={[
                  {label:"Reactivează", icon:"check", primary:true, onClick:()=>toggle(u), testid:`toggle-unit-${u.code}`, ...lock(has("org_units.deactivate"))},
                ]}><PurgeButton entity="org-unit" id={u.id} onDone={load} /></RowActions>
              </div>
            );
          })}
        </>
      ) : (
        <>
          {units.length === 0 && (
            <div className="card" style={{padding:16,marginBottom:16,borderLeft:"3px solid var(--blue-primary)",background:"var(--tint-50)",fontSize:13}}>
              <b>Nivelul 1</b> reprezintă Angajatorul (rădăcină). Adaugă mai întâi structura rădăcină, apoi subunități sub ea.
            </div>
          )}
          {units.length > 0 && (
            <div style={{display:"flex",gap:8,marginBottom:8}}>
              <button type="button" className="btn btn-secondary" style={{padding:"3px 10px",fontSize:12}} data-testid="expand-all"
                onClick={()=>setExpanded(Object.fromEntries(units.map(u=>[u.id,true])))}>Extinde tot</button>
              <button type="button" className="btn btn-secondary" style={{padding:"3px 10px",fontSize:12}} data-testid="collapse-all"
                onClick={()=>setExpanded({})}>Restrânge (nivelurile 1–2)</button>
            </div>
          )}
          <div>{renderTree("root")}</div>
          {units.length === 0 && <EmptyState text='Nicio structură. Apasă „+ Structură rădăcină".' />}
        </>
      )}

      {editing && (
        <Modal title={editing.view?"Structură — vizualizare":editing.id?"Editează structură":"Adaugă structură"} onClose={()=>setEditing(null)}>
          <ViewOnly on={editing.view}>
          <div style={{display:"grid",gap:12}}>
            <div><label className="label label-req">Cod</label>
              <input className="input" value={editing.code} onChange={e=>setEditing({...editing,code:e.target.value})} data-testid="unit-code" placeholder="ex: HQ, FC, DEP-IT" /></div>
            <div><label className="label label-req">Denumire</label>
              <input className="input" value={editing.name} onChange={e=>setEditing({...editing,name:e.target.value})} data-testid="unit-name" /></div>
            <div><label className="label">Structura părinte</label>
              <Select className="select" value={editing.parent_id||""} onChange={e=>setEditing({...editing,parent_id:e.target.value||null})} data-testid="unit-parent">
                <option value="">— (Nivel 1 · Rădăcină = Angajatorul)</option>
                {validParents(editing.id).map(u=>(
                  <option key={u.id} value={u.id}>N{u.level} · {u.code} · {u.name}</option>
                ))}
              </Select>
              <div style={{fontSize:11,color:"#64748B",marginTop:4}}>
                Nivelul se calculează automat pe baza structurii părinte selectate.
              </div>
            </div>
            <div><label className="label">Punct de lucru / Sediu</label>
              <Select className="select" value={editing.location_id||""} onChange={e=>setEditing({...editing,location_id:e.target.value})} data-testid="unit-location">
                <option value="">— moștenește de la părinte —</option>
                {locations.filter(l=>l.active).map(l=><option key={l.id} value={l.id}>{l.name}</option>)}
              </Select>
              <div style={{fontSize:11,color:"#64748B",marginTop:4}}>
                Opțional. Dacă nu selectezi, structura moștenește punctul de lucru al părintelui.
              </div>
            </div>
            <div><label className="label">Manager responsabil (Raport de muncă)</label>
              <Select className="select" value={editing.manager_employment_id||""} onChange={e=>setEditing({...editing,manager_employment_id:e.target.value})} data-testid="unit-manager">
                <option value="">— fără —</option>
                {empls.filter(e=>e.status !== "terminated").map(e => (
                  <option key={e.id} value={e.id}>{e.marca} · {e.user_name || ""}</option>
                ))}
              </Select>
            </div>
          </div>
          </ViewOnly>
          <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:18}}>
            <button className="btn btn-secondary" onClick={()=>setEditing(null)}>{editing.view?"Închide":"Anulează"}</button>
            {!editing.view && <button className="btn btn-primary" onClick={save} data-testid="save-unit" {...lock(has(editing.id?"org_units.update":"org_units.create"))}>Salvează</button>}
          </div>
        </Modal>
      )}
    </>
  );
}
